import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Inbox, Mail, Phone, MapPin, Calendar, Check, Clock } from 'lucide-react';

export const AdminInquiriesPage = () => {
  const { api } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api().get('/inquiries');
      setInquiries(res.data);
    } catch (err) {
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const updateStatus = async (id, status) => {
    try {
      await api().put(`/inquiries/${id}/status?status=${status}`);
      toast.success(`Marked as ${status}`);
      load();
    } catch (err) {
      toast.error('Update failed');
    }
  };

  const getStatusColor = (s) => ({
    new: 'bg-amber-500/20 text-amber-400 border-amber-500/50',
    contacted: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
    closed: 'bg-zinc-800 text-zinc-400 border-zinc-700'
  }[s] || 'bg-zinc-800');

  return (
    <div data-testid="admin-inquiries-page" className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-heading font-bold uppercase tracking-wide">Inquiries</h1>
        <p className="text-muted-foreground mt-1">"Need a Ref?" submissions from the public homepage.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full" />
        </div>
      ) : inquiries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-zinc-900/50 border border-zinc-800 rounded">
          <Inbox className="w-10 h-10 text-zinc-600 mb-3" />
          <p className="text-zinc-500">No inquiries yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {inquiries.map((inq, idx) => (
            <div key={inq.id} data-testid={`inquiry-card-${idx}`} className="bg-zinc-900/50 border border-zinc-800 p-5 rounded hover:border-zinc-700 transition-colors">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h3 className="font-heading font-bold text-lg">{inq.organization}</h3>
                    <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(inq.status)}`}>
                      {inq.status}
                    </span>
                    {inq.sport && <span className="px-2.5 py-0.5 text-xs bg-zinc-800 text-zinc-400 rounded-full">{inq.sport}</span>}
                  </div>
                  <p className="text-sm text-zinc-300 font-medium mb-2">{inq.contact_name}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-zinc-400">
                    <a href={`mailto:${inq.email}`} className="flex items-center gap-2 hover:text-white"><Mail className="w-4 h-4 text-amber-500" /> {inq.email}</a>
                    <a href={`tel:${inq.phone}`} className="flex items-center gap-2 hover:text-white"><Phone className="w-4 h-4 text-amber-500" /> {inq.phone}</a>
                    {inq.event_date && <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-amber-500" /> {inq.event_date}</span>}
                    {inq.location && <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-amber-500" /> {inq.location}</span>}
                  </div>
                  {inq.message && (
                    <div className="mt-3 p-3 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-300">{inq.message}</div>
                  )}
                  <p className="text-xs text-zinc-600 mt-3">Received: {new Date(inq.created_at).toLocaleString()}</p>
                </div>
                <div className="flex md:flex-col gap-2">
                  {inq.status !== 'contacted' && (
                    <Button data-testid={`mark-contacted-${idx}`} size="sm" variant="outline" onClick={() => updateStatus(inq.id, 'contacted')} className="border-zinc-700">
                      <Clock className="w-3.5 h-3.5 mr-1.5" /> Contacted
                    </Button>
                  )}
                  {inq.status !== 'closed' && (
                    <Button data-testid={`mark-closed-${idx}`} size="sm" variant="outline" onClick={() => updateStatus(inq.id, 'closed')} className="border-zinc-700">
                      <Check className="w-3.5 h-3.5 mr-1.5" /> Close
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
